/**
 * Stylitics "Wear It With" Widget for Faherty
 * Enhanced version with robust container handling and fallback creation
 */

class StyliticsWidget {
  constructor() {
    this.composer = null;
    this.productId = null;
    this.settings = window.styliticsSettings || {};
    this.isInitialized = false;
    this.retryCount = 0;
    this.maxRetries = 3;
  }

  /**
   * Initialize the widget
   */
  async init() {
    try {
      // Wait for DOM to be ready
      if (document.readyState === "loading") {
        await new Promise((resolve) => {
          document.addEventListener("DOMContentLoaded", resolve);
        });
      }

      // Additional wait for theme loading
      await this.waitForElement("body");

      await this.detectProductId();
      if (!this.productId) {
        console.warn("No product ID detected. Stylitics widget will not load.");
        return;
      }

      await this.ensureContainer();
      await this.waitForComposer();
      await this.initializeWidget();
    } catch (error) {
      console.error("Error initializing Stylitics widget:", error);

      // Retry logic
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        console.log(
          `Retrying Stylitics initialization (attempt ${this.retryCount}/${this.maxRetries})`
        );
        setTimeout(() => this.init(), 2000);
      }
    }
  }

  /**
   * Ensure the container exists, create if missing
   */
  async ensureContainer() {
    let container = document.getElementById("stylitics-composer-container");

    if (!container) {
      console.log("Container not found, creating fallback...");
      container = this.createFallbackContainer();
    }

    if (!container) {
      throw new Error("Unable to create or find Stylitics container");
    }

    return container;
  }

  /**
   * Create fallback container if template doesn't include one
   */
  createFallbackContainer() {
    // Try to find a good insertion point
    const insertionPoints = [
      // After buy buttons
      document.querySelector(".product-info__buy-buttons"),
      document.querySelector('[data-block-type="buy_buttons"]'),
      // After product info
      document.querySelector(".product-info"),
      // After main product content
      document.querySelector(".shopify-section--main-product .product"),
      // After any product section
      document.querySelector(".shopify-section--main-product"),
      // Fallback to main content
      document.querySelector("main"),
      document.querySelector("#main-content"),
      document.body,
    ];

    const insertAfter = insertionPoints.find((el) => el !== null);

    if (!insertAfter) {
      return null;
    }

    // Create container with proper styling
    const container = document.createElement("div");
    container.id = "stylitics-composer-container";
    container.className = "gutters margin-top--l stylitics-fallback-container";
    container.style.marginTop = "2rem";
    container.style.scrollMarginTop = "100px";

    // Insert after the found element
    insertAfter.parentNode.insertBefore(container, insertAfter.nextSibling);

    return container;
  }

  /**
   * Detect current product ID using multiple methods
   */
  async detectProductId() {
    // Method 1: Shopify Analytics
    if (window.ShopifyAnalytics?.meta?.product?.id) {
      this.productId = window.ShopifyAnalytics.meta.product.id.toString();
      return;
    }

    // Method 2: Meta property
    const metaProductId = document.querySelector(
      'meta[property="product:retailer_item_id"]'
    );
    if (metaProductId) {
      this.productId = metaProductId.getAttribute("content");
      return;
    }

    // Method 3: Global product object
    if (window.product?.id) {
      this.productId = window.product.id.toString();
      return;
    }

    // Method 4: Theme global
    if (window.theme?.product?.id) {
      this.productId = window.theme.product.id.toString();
      return;
    }

    // Method 5: Script tag data
    const productScripts = document.querySelectorAll(
      'script[type="application/ld+json"]'
    );
    for (const script of productScripts) {
      try {
        const data = JSON.parse(script.textContent);
        if (data["@type"] === "Product" && data.productID) {
          this.productId = data.productID.toString();
          return;
        }
      } catch (e) {
        // Continue searching
      }
    }
  }

  /**
   * Wait for StyliticsComposer to be available
   */
  async waitForComposer(timeout = 10000) {
    const startTime = Date.now();

    while (!window.StyliticsComposer) {
      if (Date.now() - startTime > timeout) {
        throw new Error("StyliticsComposer not loaded within timeout");
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  /**
   * Wait for element to exist
   */
  async waitForElement(selector, timeout = 5000) {
    const startTime = Date.now();

    while (!document.querySelector(selector)) {
      if (Date.now() - startTime > timeout) {
        throw new Error(`Element ${selector} not found within timeout`);
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    return document.querySelector(selector);
  }

  /**
   * Initialize the Stylitics widget
   */
  async initializeWidget() {
    try {
      // Get customer data - prioritize reliable Liquid-generated data
      const customerData = window.styliticsCustomer || {};
      const customerId = customerData.id || window.__VPV?.customer?.id || null;
      const customerEmail =
        customerData.email || window.__VPV?.customer?.email || null;

      this.composer = await StyliticsComposer.initialize({
        client: "faherty",
        target: "stylitics-composer-container",
        config: {
          modules: [
            {
              type: "classic",
              params: {
                api: {
                  item_number: this.productId,
                  min: this.settings.minBundles || 3,
                  max: this.settings.maxBundles || 6,
                },
                text: {
                  itemLinkCTA: this.settings.buttonText || "Shop",
                  moduleHeading: this.settings.heading || "Wear It With",
                  moduleHeadingLevel: "h4",
                  modalHeadingLevel: "h6",
                },
              },
              on: {
                "mount.bundles": (data) => this.handleBundlesMount(data),
                "mount.bundle": (data) => this.handleBundleMount(data),
                "mount.error": (error) => {
                  console.error("Stylitics mount error:", error);
                },
                "api.error": (error) => {
                  console.error("Stylitics API error:", error);
                },
                "load.data": (data) => this.handleLoadData(data),
              },
              override: {
                "click.item": (data) => this.handleItemClick(data),
                "click.hotspot": (data) => this.handleHotspotClick(data),
              },
            },
          ],
          sharedParams: {
            customer: customerId
              ? {
                  clientUserId: customerId,
                  ...(customerEmail && { email: customerEmail }),
                }
              : {},
            analytics: {
              placement: "pdp",
              // Enable comprehensive tracking
              enableClickTracking: true,
              enableViewTracking: true,
              enableEngagementTracking: true,
            },
          },
        },
      });

      // Start the composer to trigger API calls and bundle loading
      await this.composer.start();

      this.isInitialized = true;
      this.setupVariantChangeListener();
      this.setupAddToCartTracking();

      // Initialize interaction tracking
      this._lastStyliticsInteraction = null;
      this._miniModalFromStylitics = false;
      this._miniModalOpenTime = null;
    } catch (error) {
      console.error("Error initializing Stylitics widget:", error);
      throw error;
    }
  }

  /**
   * Handle bundles mount event
   */
  handleBundlesMount(data) {
    if (!data.bundles || data.bundles.length === 0) {
      this.hideWidget();
      return;
    }

    const minBundles = this.settings.minBundles || 3;

    if (data.bundles.length >= minBundles) {
      this.showWidget();

      // Wait for content to render, then create jumplink
      setTimeout(() => {
        const container = document.getElementById(
          "stylitics-composer-container"
        );

        if (container && container.innerHTML.length > 100) {
          this.createJumpLinkIfNeeded();
        } else {
          // Retry after another delay
          setTimeout(() => this.createJumpLinkIfNeeded(), 1000);
        }
      }, 2000);
    } else {
      this.hideWidget();
    }
  }

  /**
   * Handle individual bundle mount for custom badges
   */
  handleBundleMount(data) {
    if (!data.bundle || !data.element) return;

    // Add "Style the Model" badge for on-model images
    if (data.bundle["on-model-image"]) {
      const badgeContainer = data.element.querySelector(
        ".stylitics-bundle-badge"
      );
      if (
        badgeContainer &&
        !badgeContainer.querySelector(".stylitics-stm-badge")
      ) {
        const badge = document.createElement("img");
        badge.className = "stylitics-stm-badge";
        badge.src =
          "https://cdn.shopify.com/s/files/1/0359/8357/files/STM.png?v=1736387449";
        badge.alt = "This outfit corresponds with model's on product image";
        badgeContainer.appendChild(badge);
      }
    }
  }

  /**
   * Handle load.data event - proper way to control jumplink visibility
   * Documentation: https://docs.stylitics.com/hc/en-us/articles/24049134586907-Add-a-Title-and-Jumplink
   */
  handleLoadData(data) {
    if (!data.hidden) {
      this.showWidget();
    } else {
      this.hideWidget();
    }
  }

  /**
   * Handle item click - proper way to intercept product clicks
   * Documentation: https://docs.stylitics.com/hc/en-us/articles/24049676465691-Quickview-Integration-Item-Click
   * Note: Stylitics automatically fires the item.click tracking event, no manual tracking needed
   */
  handleItemClick(data) {
    // Extract product handle from the affiliate link
    const url = new URL(data.item.affiliate_link);
    const pathParts = url.pathname.split("/");
    const handle = pathParts[pathParts.indexOf("products") + 1];
    const variantId = url.searchParams.get("variant");

    // Dispatch mini-modal:open event
    window.dispatchEvent(
      new CustomEvent("mini-modal:open", {
        detail: {
          handle: handle,
          variantId: variantId || null,
        },
      })
    );

    // Mark this as a Stylitics interaction for add-to-cart tracking
    this._lastStyliticsInteraction = Date.now();
  }

  /**
   * Handle hotspot click - proper way to intercept hotspot clicks on outfit images
   * Note: Stylitics automatically fires the hotspot.click tracking event, no manual tracking needed
   */
  handleHotspotClick(data) {
    // The hotspot data contains the item information
    if (!data.item || !data.item.affiliate_link) {
      console.warn("Stylitics: Hotspot click missing item data");
      return;
    }

    // Extract product handle from the affiliate link
    const url = new URL(data.item.affiliate_link);
    const pathParts = url.pathname.split("/");
    const handle = pathParts[pathParts.indexOf("products") + 1];
    const variantId = url.searchParams.get("variant");

    // Dispatch mini-modal:open event
    window.dispatchEvent(
      new CustomEvent("mini-modal:open", {
        detail: {
          handle: handle,
          variantId: variantId || null,
        },
      })
    );

    // Mark this as a Stylitics interaction for add-to-cart tracking
    this._lastStyliticsInteraction = Date.now();
  }

  /**
   * Show the widget and jump link
   */
  showWidget() {
    const container = document.getElementById("stylitics-composer-container");
    if (container) {
      container.style.display = "block";
      container.style.visibility = "visible";
    }

    const jumpLink = document.getElementById("stylitics-jumplink");
    if (jumpLink) {
      jumpLink.style.visibility = "visible";
      jumpLink.style.display = "block";
    }

    // Show widget heading
    const heading = document.querySelector(
      '[data-testid="stylitics-ui-heading"]'
    );
    if (heading) {
      heading.style.visibility = "visible";
    }
  }

  /**
   * Hide the widget and jump link
   */
  hideWidget() {
    const container = document.getElementById("stylitics-composer-container");
    if (container) {
      container.style.display = "none";
    }

    const jumpLink = document.getElementById("stylitics-jumplink");
    if (jumpLink) {
      jumpLink.style.visibility = "hidden";
      jumpLink.style.display = "none";
    }
  }

  /**
   * Create jump link badge in the top right of product gallery
   * This is called after Stylitics content is loaded to ensure proper positioning
   */
  createJumpLinkIfNeeded() {
    if (!this.settings.showJumpLink) return;

    let jumpLink = document.getElementById("stylitics-jumplink");
    if (jumpLink) {
      jumpLink.style.visibility = "visible";
      jumpLink.style.display = "block";
      return;
    }

    // Look for product gallery/media containers to position the badge
    const galleryContainers = [
      // Try to find the main product media container
      document.querySelector(".product__media"),
      document.querySelector(".product-media"),
      document.querySelector("[data-media-gallery]"),
      document.querySelector(".product-gallery"),
      // Fallback to any media container
      document.querySelector(".media"),
      document.querySelector("[class*='media']"),
      // Last resort - main product section
      document.querySelector(".shopify-section--main-product"),
    ];

    const galleryContainer = galleryContainers.find((el) => el !== null);
    if (!galleryContainer) return;

    // Create jump link badge positioned in top right of gallery
    jumpLink = document.createElement("a");
    jumpLink.id = "stylitics-jumplink";
    jumpLink.href = "#stylitics-composer-container";
    jumpLink.className = "stylitics-jump-badge";
    jumpLink.style.cssText = `
      position: absolute;
      z-index: 10;
      display: inline-flex;
      align-items: center;
      padding: 8px 12px;
      background: #fcfaf6;
      color: #2b2e2f;
      text-decoration: none;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      transition: all 0.3s ease;
      backdrop-filter: blur(4px);
      visibility: visible;
      box-shadow:0 0 0 1px rgb(var(--text-color) / .12);
    `;

    jumpLink.innerHTML = `
      ${this.settings.heading || "Wear It With"} 
      <svg style="margin-left: 6px; width: 10px; height: 6px;" viewBox="0 0 12 8" fill="currentColor">
        <path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    // Add hover effects
    jumpLink.addEventListener("mouseenter", () => {
      jumpLink.style.background = "#2b2e2f";
      jumpLink.style.color = "#FFFFFF";
      jumpLink.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
    });

    jumpLink.addEventListener("mouseleave", () => {
      jumpLink.style.background = "rgba(238, 237, 224, 0.95)";
      jumpLink.style.color = "#2b2e2f";
      jumpLink.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
    });

    // Add smooth scroll behavior with proper offset
    jumpLink.addEventListener("click", (e) => {
      e.preventDefault();
      const container = document.getElementById("stylitics-composer-container");
      if (!container) return;

      // Use requestAnimationFrame to ensure layout is complete
      requestAnimationFrame(() => {
        const headerOffset = 100;
        const elementPosition = container.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      });
    });

    // Ensure the gallery container has relative positioning
    const galleryPosition = window.getComputedStyle(galleryContainer).position;
    if (galleryPosition === "static") {
      galleryContainer.style.position = "relative";
    }

    // Insert the badge into the gallery container
    galleryContainer.appendChild(jumpLink);
  }

  /**
   * Track add-to-cart for analytics
   */
  trackAddToCart(cartInfo) {
    // Track with Stylitics native API - try trackAddItemToCart first, then fallback to trackClick
    const module = this.composer?.modules?.[0];

    if (module?.trackAddItemToCart) {
      try {
        module.trackAddItemToCart({
          item_number: cartInfo.productId,
          variant_id: cartInfo.variantId,
          quantity: cartInfo.quantity,
          price: cartInfo.priceInCents || cartInfo.price * 100, // Stylitics expects price in cents
          placement: "pdp",
          source: cartInfo.source,
        });
        console.log("Stylitics add-to-cart tracked (trackAddItemToCart):", {
          productId: cartInfo.productId,
          variantId: cartInfo.variantId,
          quantity: cartInfo.quantity,
          source: cartInfo.source,
        });
      } catch (error) {
        console.warn("Stylitics trackAddItemToCart error:", error);
      }
    } else {
      console.warn("Stylitics trackAddItemToCart method not available");
    }
  }

  /**
   * Refresh widget for new product
   */
  async refresh(newProductId) {
    if (!this.isInitialized || !this.composer) {
      return this.init();
    }

    if (this.productId === newProductId) {
      return;
    }

    this.productId = newProductId;

    try {
      await this.composer.refresh({
        modules: [
          {
            type: "classic",
            params: {
              api: {
                item_number: newProductId,
                min: this.settings.minBundles || 3,
                max: this.settings.maxBundles || 6,
              },
            },
          },
        ],
      });
    } catch (error) {
      console.error("Stylitics: Refresh failed:", error);
    }
  }

  /**
   * Setup listener for product changes when color swatches are clicked
   */
  setupVariantChangeListener() {
    // Hook directly into swatch clicks
    document.addEventListener("click", (event) => {
      // Guard against event.target not being a DOM element
      if (!event.target || typeof event.target.closest !== 'function') {
        return;
      }
      const swatchElement =
        event.target.closest(".product-swatch") ||
        event.target.closest('[class*="swatch"]') ||
        event.target.closest(".variant-picker button") ||
        event.target.closest(".product-grouping button");

      if (swatchElement) {
        setTimeout(() => {
          const newProductId = this.getUpdatedProductId();
          if (newProductId && newProductId !== this.productId) {
            this.refresh(newProductId);
          }
        }, 1000);
      }
    });
  }

  /**
   * Get the updated product ID from the DOM after color swatch changes
   */
  getUpdatedProductId() {
    const mainProductInfo = document.querySelector("#main-product-info");
    if (mainProductInfo) {
      const productId = mainProductInfo.getAttribute("data-product-id");
      if (productId) {
        return productId;
      }
    }

    if (window.ShopifyAnalytics?.meta?.product?.id) {
      return window.ShopifyAnalytics.meta.product.id.toString();
    }

    return null;
  }

  /**
   * Setup tracking for add-to-cart events from the main theme
   */
  setupAddToCartTracking() {
    // Listen for Shopify's variant:add events (from main theme)
    document.addEventListener("variant:add", (event) => {
      console.log("variant:add event detected:", event);

      // Check if this add-to-cart came from Stylitics widget
      const styliticsContainer = document.getElementById(
        "stylitics-composer-container"
      );

      console.log("Stylitics container:", styliticsContainer);
      console.log("Event target:", event.target);
      console.log(
        "Container contains target:",
        styliticsContainer &&
          event.target &&
          styliticsContainer.contains(event.target)
      );

      if (
        styliticsContainer &&
        event.target &&
        styliticsContainer.contains(event.target)
      ) {
        console.log("Add-to-cart from Stylitics widget detected!");
        const detail = event.detail;
        const items = detail.items || [detail];

        console.log("Cart items to track:", items);

        items.forEach((item) => {
          this.trackAddToCart({
            productId: item.product_id,
            variantId: item.variant_id,
            quantity: item.quantity,
            price: item.price ? item.price / 100 : null, // Convert cents to dollars
            priceInCents: item.price, // Keep original for reference
            handle: item.handle,
            source: "stylitics_widget_variant_add",
          });
        });
      } else {
        console.log("Add-to-cart not from Stylitics widget, ignoring");
      }
    });

    // Listen for mini-modal open events
    document.addEventListener("mini-modal:open", (event) => {
      // Mark that a mini-modal was opened from Stylitics
      if (
        this._lastStyliticsInteraction &&
        Date.now() - this._lastStyliticsInteraction < 2000
      ) {
        this._miniModalFromStylitics = true;
        this._miniModalOpenTime = Date.now();
      }
    });

    // Also listen for variant:add events from mini-modal
    document.addEventListener("variant:add", (event) => {
      // If mini-modal was opened from Stylitics, track this add-to-cart
      if (
        this._miniModalFromStylitics &&
        this._miniModalOpenTime &&
        Date.now() - this._miniModalOpenTime < 60000
      ) {
        const detail = event.detail;
        const items = detail.items || [detail];

        items.forEach((item) => {
          const itemId = item.product_id;
          if (itemId && this.composer?.modules?.[0]?.trackAddItemToCart) {
            this.composer.modules[0].trackAddItemToCart(itemId);
          }
        });

        // Clear the flag after tracking
        this._miniModalFromStylitics = false;
        this._miniModalOpenTime = null;
      } else {
        // Check if this was a recent Stylitics interaction (backup method)
        const recentStyliticsActivity =
          this._lastStyliticsInteraction &&
          Date.now() - this._lastStyliticsInteraction < 10000;

        if (recentStyliticsActivity) {
          const detail = event.detail;
          const items = detail.items || [detail];

          items.forEach((item) => {
            const itemId = item.product_id;
            if (itemId && this.composer?.modules?.[0]?.trackAddItemToCart) {
              this.composer.modules[0].trackAddItemToCart(itemId);
            }
          });
        }
      }
    });

    // Mark Stylitics interactions for tracking
    const styliticsContainer = document.getElementById(
      "stylitics-composer-container"
    );
    if (styliticsContainer) {
      styliticsContainer.addEventListener("click", () => {
        this._lastStyliticsInteraction = Date.now();
      });
    }
  }
}

// Initialize widget when DOM is ready
(function () {
  let styliticsWidget;

  function initWidget() {
    if (!styliticsWidget) {
      styliticsWidget = new StyliticsWidget();
      // Expose instance for manual debugging after creation
      window.styliticsWidget = styliticsWidget;
    }
    styliticsWidget.init();
  }

  // Multiple initialization triggers to ensure the widget loads
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initWidget);
  } else {
    initWidget();
  }

  // Also initialize on theme section loads (for AJAX navigation)
  document.addEventListener("shopify:section:load", (e) => {
    if (e.detail.sectionId.includes("main-product")) {
      setTimeout(initWidget, 100);
    }
  });

  // Expose class for manual debugging
  window.StyliticsWidget = StyliticsWidget;
})();
